import { ColumnDef, FilterFn } from "@tanstack/react-table";
import { match } from "ts-pattern";
import { arrayCheckboxFilter, checkboxFilter } from "../../FilterableDataTable";
import {
	DynamicDataTableColumn,
	DynamicDataTableColumnFilterKind,
	DynamicDataTableTanStackMeta,
} from "../types";

function metaHeaderFilterTypeForFilterKind(kind: DynamicDataTableColumnFilterKind): "checkbox" {
	return match(kind)
		.with("checkbox", "checkbox-array", () => "checkbox" as const)
		.exhaustive();
}

type ColumnAffordancePick = Pick<
	DynamicDataTableColumn<object>,
	"cellStyle" | "headerFilter" | "csv"
>;

export function toTanStackColumnMeta(
	author: ColumnAffordancePick | undefined,
): DynamicDataTableTanStackMeta | undefined {
	if (author == null) return undefined;

	const out: DynamicDataTableTanStackMeta = {};
	const { cellStyle: bodyCell, headerFilter, csv } = author;

	if (headerFilter) {
		out.headerFilterType = metaHeaderFilterTypeForFilterKind(headerFilter.type);
	}
	if (bodyCell?.noTruncate === true) {
		out.noTruncate = true;
	}
	if (bodyCell?.centerItems === true) {
		out.centerItems = true;
	}
	if (csv?.rowObjectKey) {
		out.csvKey = csv.rowObjectKey;
	}
	if (csv?.fileColumnHeader) {
		out.csvLabel = csv.fileColumnHeader;
	}
	if (csv?.formatValue) {
		out.csvFormatter = csv.formatValue;
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Maps a `DynamicDataTableColumnFilterKind` to the TanStack `FilterFn` used for that column.
 */
function resolveFilterFnByColumnFilterKind(
	kind: DynamicDataTableColumnFilterKind,
): FilterFn<unknown> {
	return match(kind)
		.with("checkbox", () => checkboxFilter as FilterFn<unknown>)
		.with("checkbox-array", () => arrayCheckboxFilter as FilterFn<unknown>)
		.exhaustive();
}

function toTanStackColumnDef<TData extends object>(
	col: DynamicDataTableColumn<TData>,
): ColumnDef<TData, unknown> {
	const { cell, cellStyle, headerFilter, csv, ...rest } = col;
	const filterFromKind = headerFilter
		? resolveFilterFnByColumnFilterKind(headerFilter.type)
		: undefined;
	return {
		...rest,
		...(filterFromKind !== undefined ? { filterFn: filterFromKind } : {}),
		header: col.header,
		...(cell !== undefined ? { cell: (context) => cell({ row: context.row }) } : {}),
		meta: toTanStackColumnMeta({ cellStyle, headerFilter, csv }),
	} as ColumnDef<TData, unknown>;
}

export function toTanStackColumnDefs<TData extends object>(
	columns: readonly DynamicDataTableColumn<TData>[],
): ColumnDef<TData, unknown>[] {
	return columns.map((c) => toTanStackColumnDef(c));
}
